"use client";

import { useState, useMemo } from "react";
import { useRouter } from "next/navigation";
import { useSearchParams } from "next/navigation";

type ConnectLocation = {
  id: number;
  name: string;
  slug: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  phone: string | null;
};

type LocationPickerProps = {
  locations: ConnectLocation[];
  onSelectLocation: (location: ConnectLocation) => void;
  orgSlug: string;
  isValidOrgDomain: boolean;
  isEmbed?: boolean;
};

export default function LocationPicker({
  locations,
  onSelectLocation,
  orgSlug,
  isValidOrgDomain,
  isEmbed,
}: LocationPickerProps) {
  const [search, setSearch] = useState("");
  const router = useRouter();
  const searchParams = useSearchParams();

  const filtered = useMemo(() => {
    if (!search.trim()) return locations;
    const q = search.toLowerCase().trim();
    return locations.filter(
      (loc) =>
        loc.name.toLowerCase().includes(q) ||
        (loc.city && loc.city.toLowerCase().includes(q)) ||
        (loc.zipCode && loc.zipCode.includes(q)) ||
        (loc.address && loc.address.toLowerCase().includes(q)) ||
        (loc.state && loc.state.toLowerCase().includes(q))
    );
  }, [locations, search]);

  const handleSelect = (loc: ConnectLocation) => {
    if (loc.slug) {
      const embedSuffix = isEmbed ? "/embed" : "";
      const basePath = isValidOrgDomain
        ? `/${loc.slug}${embedSuffix}`
        : `/org/${orgSlug}/${loc.slug}${embedSuffix}`;

      const existingParams = searchParams.toString();
      const locationUrl = existingParams ? `${basePath}?${existingParams}` : basePath;
      router.push(locationUrl);
    } else {
      onSelectLocation(loc);
    }
  };

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-6 text-center">
        <h3 className="text-emphasis text-lg font-semibold">Select a Location</h3>
        <p className="text-subtle text-sm mt-1">Choose a location to see available appointments</p>
      </div>

      {locations.length >= 2 && (
        <div className="mb-4">
          <input
            type="text"
            placeholder="Search by name, city, or zip code..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border-subtle bg-default text-default placeholder:text-muted w-full rounded-md border px-4 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="text-subtle py-8 text-center text-sm">
          No locations found matching "{search}"
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {filtered.map((loc) => {
            const addressParts = [loc.address, loc.city, loc.state, loc.zipCode]
              .filter(Boolean)
              .join(", ");

            return (
              <button
                key={loc.id}
                type="button"
                onClick={() => handleSelect(loc)}
                className="bg-default hover:bg-cal-muted border-subtle group flex flex-col rounded-lg border p-4 text-left transition-colors">
                <div className="flex items-start gap-3 w-full">
                  <div className="bg-subtle flex h-10 w-10 shrink-0 items-center justify-center rounded-lg">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-emphasis">
                      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-emphasis text-sm font-semibold truncate">{loc.name}</p>
                    {addressParts && (
                      <p className="text-subtle mt-0.5 text-xs truncate">{addressParts}</p>
                    )}
                    {loc.phone && (
                      <p className="text-subtle mt-0.5 text-xs">{loc.phone}</p>
                    )}
                  </div>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-subtle mt-0.5 shrink-0 transition-transform group-hover:translate-x-0.5">
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
